import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Search,
  Filter,
  Eye,
  DollarSign,
  Calendar,
  User,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  CreditCard,
  FileText,
  RefreshCw,
  ChevronRight,
  X as CloseIcon,
  Bell,
  Settings,
  Trash2,
  History
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useDataRefresh, ENTITY_TYPES } from '../context/DataRefreshContext';
import { UserMenu } from '../components/UserMenu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Formatear precio en formato colombiano
const formatPrice = (value) => {
  if (!value && value !== 0) return '$ 0';
  return `$ ${Math.round(value).toLocaleString('es-CO')}`;
};

// Formatear fecha
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  } catch {
    return dateString;
  }
};

// Estados de orden de venta con colores
const ESTADOS_VENTA = {
  pendiente_primer_pago: { 
    label: 'Pendiente', 
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: Clock 
  },
  al_dia: { 
    label: 'Al día', 
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle 
  },
  pago_parcial: { 
    label: 'Pago parcial', 
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: CreditCard 
  },
  cuota_vencida: { 
    label: 'Cuota vencida', 
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: AlertTriangle 
  },
  pagada_totalmente: { 
    label: 'Pagada', 
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    icon: CheckCircle 
  },
  en_produccion: { 
    label: 'En producción', 
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    icon: FileText 
  },
  entregada: { 
    label: 'Entregada', 
    color: 'bg-zinc-100 text-zinc-800 border-zinc-200',
    icon: CheckCircle 
  },
  cancelada: { 
    label: 'Cancelada', 
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: CloseIcon 
  }
};

const VentasList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { notifyDeletion, refreshTimestamps } = useDataRefresh();
  const [ordenes, setOrdenes] = useState([]);
  const [metricas, setMetricas] = useState(null);
  const [alertas, setAlertas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState(searchParams.get('estado') || '');
  const [filterVencidas, setFilterVencidas] = useState(searchParams.get('vencidas') === 'true');
  const [filterArchivadas, setFilterArchivadas] = useState(searchParams.get('archivadas') === 'true');
  const [showFilters, setShowFilters] = useState(false);
  const [showAlertasPanel, setShowAlertasPanel] = useState(false);
  const [diasAnticipacion, setDiasAnticipacion] = useState(7);

  // Estado para borrado lógico (solo Gerencia)
  const [showEliminarModal, setShowEliminarModal] = useState(false);
  const [ventaParaEliminar, setVentaParaEliminar] = useState(null);
  const [dependenciasVenta, setDependenciasVenta] = useState(null);
  const [cargandoDependencias, setCargandoDependencias] = useState(false);
  const [motivoEliminacion, setMotivoEliminacion] = useState('');
  const [confirmacionNumero, setConfirmacionNumero] = useState('');
  const [eliminando, setEliminando] = useState(false);
  const esGerencia = user?.role === 'gerencia' || user?.role === 'admin';

  // Cargar métricas
  const fetchMetricas = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ventas/dashboard/metricas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetricas(response.data);
    } catch (error) {
      console.error('Error al cargar métricas:', error);
    }
  }, []);

  // Cargar alertas de cuotas
  const fetchAlertas = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/ventas/alertas/cuotas-proximas?dias_anticipacion=${diasAnticipacion}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlertas(response.data);
    } catch (error) {
      console.error('Error al cargar alertas:', error);
    }
  }, [diasAnticipacion]);

  // Cargar órdenes de venta
  const fetchOrdenes = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (search) params.append('cliente', search);
      if (filterEstado) params.append('estado', filterEstado);
      if (filterVencidas) params.append('vencidas', 'true');
      if (filterArchivadas) params.append('incluir_eliminadas', 'true');
      
      const response = await axios.get(`${API_URL}/api/ventas?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrdenes(response.data.ordenes || []);
    } catch (error) {
      console.error('Error al cargar órdenes:', error);
      toast.error('Error al cargar las órdenes de venta');
    } finally {
      setLoading(false);
    }
  }, [search, filterEstado, filterVencidas, filterArchivadas]);

  useEffect(() => {
    fetchOrdenes();
    fetchMetricas();
    fetchAlertas();
  }, [fetchOrdenes, fetchMetricas, fetchAlertas]);

  // Función para abrir modal de eliminación de venta
  const abrirModalEliminarVenta = async (venta) => {
    if (!esGerencia) {
      toast.error('Solo Gerencia puede eliminar ventas');
      return;
    }
    
    setVentaParaEliminar(venta);
    setMotivoEliminacion('');
    setConfirmacionNumero('');
    setDependenciasVenta(null);
    setCargandoDependencias(true);
    setShowEliminarModal(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/gerencia/venta/${venta.id}/analisis-eliminacion`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDependenciasVenta(response.data);
    } catch (error) {
      console.error('Error cargando dependencias:', error);
      toast.error('Error al verificar dependencias de la venta');
      setShowEliminarModal(false);
    } finally {
      setCargandoDependencias(false);
    }
  };

  const confirmarEliminacionVenta = async () => {
    if (!ventaParaEliminar) return;
    
    if (!motivoEliminacion.trim() || motivoEliminacion.length < 10) {
      toast.error('El motivo debe tener al menos 10 caracteres');
      return;
    }
    
    if (confirmacionNumero.trim().toUpperCase() !== ventaParaEliminar.numero.toUpperCase()) {
      toast.error('El número de venta no coincide');
      return;
    }
    
    setEliminando(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/gerencia/venta/${ventaParaEliminar.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: {
          motivo: motivoEliminacion,
          confirmar_nombre: confirmacionNumero
        }
      });
      toast.success('Venta eliminada lógicamente. Puede restaurarse desde Registros Eliminados.');
      setShowEliminarModal(false);
      setVentaParaEliminar(null);
      
      // Notificar invalidación de caché para actualizar todas las vistas
      notifyDeletion(ENTITY_TYPES.VENTAS, { cliente_id: ventaParaEliminar.cliente_id });
      
      fetchOrdenes();
      fetchMetricas();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Error al eliminar venta');
    } finally {
      setEliminando(false);
    }
  };

  // Debounce para búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchOrdenes();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, filterEstado, filterVencidas, filterArchivadas, fetchOrdenes]);

  // Refrescar datos cuando otra vista invalide el caché de ventas
  useEffect(() => {
    fetchOrdenes();
    fetchMetricas();
    fetchAlertas();
  }, [refreshTimestamps.ventas]);

  // Actualizar URL cuando cambian filtros
  useEffect(() => {
    const params = new URLSearchParams();
    if (filterEstado) params.set('estado', filterEstado);
    if (filterVencidas) params.set('vencidas', 'true');
    if (filterArchivadas) params.set('archivadas', 'true');
    setSearchParams(params);
  }, [filterEstado, filterVencidas, filterArchivadas, setSearchParams]);

  const getEstadoConfig = (estado) => {
    return ESTADOS_VENTA[estado] || ESTADOS_VENTA.pendiente_primer_pago;
  };

  const totalAlertas = (alertas?.total_vencidas || 0) + (alertas?.total_proximas || 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900">
      {/* Header */}
      <header className="bg-zinc-900/80 backdrop-blur-md border-b border-zinc-700/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-zinc-400" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-white">Órdenes de Venta</h1>
                <p className="text-sm text-zinc-400">Gestión de ventas confirmadas y pagos</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Botón de alertas */}
              <button
                onClick={() => setShowAlertasPanel(!showAlertasPanel)}
                className={`relative p-2 rounded-lg transition-colors ${
                  totalAlertas > 0 ? 'bg-red-500/20 hover:bg-red-500/30' : 'hover:bg-zinc-800'
                }`}
                title="Alertas de cuotas"
              >
                <Bell className={`w-5 h-5 ${totalAlertas > 0 ? 'text-red-400' : 'text-zinc-400'}`} />
                {totalAlertas > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {totalAlertas}
                  </span>
                )}
              </button>
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Métricas */}
        {metricas && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-zinc-800/50 backdrop-blur-sm rounded-xl p-4 border border-zinc-700/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <FileText className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-400">Total Ventas</p>
                  <p className="text-xl font-bold text-white">{metricas.total_ventas}</p>
                </div>
              </div>
            </div>

            <div className="bg-zinc-800/50 backdrop-blur-sm rounded-xl p-4 border border-zinc-700/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-400">Recaudado</p>
                  <p className="text-lg font-bold text-emerald-400">{formatPrice(metricas.dinero_recaudado)}</p>
                </div>
              </div>
            </div>

            <div className="bg-zinc-800/50 backdrop-blur-sm rounded-xl p-4 border border-zinc-700/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-400">Cartera Pendiente</p>
                  <p className="text-lg font-bold text-blue-400">{formatPrice(metricas.cartera_pendiente)}</p>
                </div>
              </div>
            </div>

            <div className="bg-zinc-800/50 backdrop-blur-sm rounded-xl p-4 border border-zinc-700/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-xs text-zinc-400">Ventas en mora</p>
                  <p className="text-xl font-bold text-red-400">{metricas.cuotas_vencidas}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Panel de Alertas */}
        {showAlertasPanel && alertas && (
          <div className="bg-zinc-800/50 backdrop-blur-sm border border-zinc-700/50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-400" />
                Alertas de Cuotas
              </h3>
              <div className="flex items-center gap-2">
                <label className="text-sm text-zinc-400">Días anticipación:</label>
                <select
                  value={diasAnticipacion}
                  onChange={(e) => setDiasAnticipacion(parseInt(e.target.value))}
                  className="bg-zinc-700 border border-zinc-600 rounded-lg px-2 py-1 text-white text-sm"
                >
                  <option value={3}>3 días</option>
                  <option value={7}>7 días</option>
                  <option value={14}>14 días</option>
                  <option value={30}>30 días</option>
                </select>
              </div>
            </div>

            {alertas.total_vencidas === 0 && alertas.total_proximas === 0 ? (
              <p className="text-zinc-400 text-center py-4">No hay alertas pendientes</p>
            ) : (
              <div className="space-y-4">
                {/* Cuotas Vencidas */}
                {alertas.vencidas?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-red-400 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Cuotas Vencidas ({alertas.total_vencidas})
                    </h4>
                    <div className="space-y-2">
                      {alertas.vencidas.map((alerta, idx) => (
                        <div 
                          key={idx}
                          onClick={() => navigate(`/ventas/${alerta.orden_id}`)}
                          className="flex items-center justify-between bg-red-500/10 border border-red-500/30 rounded-lg p-3 cursor-pointer hover:bg-red-500/20"
                        >
                          <div>
                            <span className="text-amber-400 font-mono text-sm">{alerta.orden_numero}</span>
                            <span className="text-white ml-2">{alerta.cliente_nombre}</span>
                            <p className="text-sm text-zinc-400">
                              Cuota {alerta.cuota_numero} - Vencida hace {alerta.dias_vencida} días
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-red-400 font-bold">{formatPrice(alerta.saldo_cuota)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cuotas Próximas */}
                {alertas.proximas?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-amber-400 mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Próximas a Vencer ({alertas.total_proximas})
                    </h4>
                    <div className="space-y-2">
                      {alertas.proximas.map((alerta, idx) => (
                        <div 
                          key={idx}
                          onClick={() => navigate(`/ventas/${alerta.orden_id}`)}
                          className="flex items-center justify-between bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 cursor-pointer hover:bg-amber-500/20"
                        >
                          <div>
                            <span className="text-amber-400 font-mono text-sm">{alerta.orden_numero}</span>
                            <span className="text-white ml-2">{alerta.cliente_nombre}</span>
                            <p className="text-sm text-zinc-400">
                              Cuota {alerta.cuota_numero} - Vence en {alerta.dias_restantes} días
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-amber-400 font-bold">{formatPrice(alerta.saldo_cuota)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Barra de búsqueda y filtros */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <input
              type="text"
              placeholder="Buscar por cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-zinc-800/50 border border-zinc-700/50 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/50 min-h-[48px]"
            />
          </div>

          {/* Botones de filtro en fila para móvil */}
          <div className="flex gap-2 sm:gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex-1 sm:flex-none px-4 py-3 rounded-xl border transition-colors flex items-center justify-center gap-2 min-h-[48px] ${
                showFilters || filterEstado 
                  ? 'bg-amber-500/20 border-amber-500/50 text-amber-400' 
                  : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-400 hover:bg-zinc-700/50'
              }`}
            >
              <Filter className="w-5 h-5" />
              <span className="hidden sm:inline">Filtros</span>
              {filterEstado && <span className="w-2 h-2 bg-amber-400 rounded-full"></span>}
            </button>

            {/* Toggle Archivadas - Solo Gerencia */}
            {esGerencia && (
              <button
                onClick={() => setFilterArchivadas(!filterArchivadas)}
                className={`flex-1 sm:flex-none px-4 py-3 rounded-xl border transition-colors flex items-center justify-center gap-2 min-h-[48px] ${
                  filterArchivadas 
                    ? 'bg-purple-500/20 border-purple-500/50 text-purple-400' 
                    : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-400 hover:bg-zinc-700/50'
                }`}
                title="Mostrar ventas archivadas/eliminadas"
                data-testid="toggle-archivadas"
              >
                <History className="w-5 h-5" />
                <span className="hidden sm:inline">Archivadas</span>
                {filterArchivadas && <span className="w-2 h-2 bg-purple-400 rounded-full"></span>}
              </button>
            )}

            <button
              onClick={() => { fetchOrdenes(); fetchMetricas(); }}
              className="px-4 py-3 bg-zinc-800/50 border border-zinc-700/50 rounded-xl text-zinc-400 hover:bg-zinc-700/50 transition-colors min-h-[48px] flex items-center justify-center"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Panel de filtros */}
        {showFilters && (
          <div className="bg-zinc-800/50 backdrop-blur-sm border border-zinc-700/50 rounded-xl p-4 mb-6">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterEstado('')}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  !filterEstado 
                    ? 'bg-amber-500 text-white' 
                    : 'bg-zinc-700/50 text-zinc-300 hover:bg-zinc-600/50'
                }`}
              >
                Todos
              </button>
              {Object.entries(ESTADOS_VENTA).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => setFilterEstado(key)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    filterEstado === key 
                      ? 'bg-amber-500 text-white' 
                      : 'bg-zinc-700/50 text-zinc-300 hover:bg-zinc-600/50'
                  }`}
                >
                  {config.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Lista de órdenes */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : ordenes.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-zinc-400 mb-2">No hay órdenes de venta</h3>
            <p className="text-zinc-500 mb-6">Las órdenes se crean al confirmar una cotización</p>
            <button
              onClick={() => navigate('/quotes')}
              className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl transition-colors"
            >
              Ver Cotizaciones
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {ordenes.map((orden) => {
              const estadoConfig = getEstadoConfig(orden.estado);
              const EstadoIcon = estadoConfig.icon;
              const porcentajePagado = orden.porcentaje_pagado || 0;

              return (
                <div
                  key={orden.id}
                  onClick={() => navigate(`/ventas/${orden.id}`)}
                  className={`bg-zinc-800/50 backdrop-blur-sm border rounded-xl p-4 hover:border-amber-500/30 hover:bg-zinc-800/70 transition-all cursor-pointer group ${
                    orden.eliminado ? 'border-purple-500/50 opacity-75' : 'border-zinc-700/50'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    {/* Info principal */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-amber-400 font-mono font-semibold">{orden.numero}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${estadoConfig.color}`}>
                          <EstadoIcon className="w-3 h-3 inline mr-1" />
                          {estadoConfig.label}
                        </span>
                        {/* Badge de archivada */}
                        {orden.eliminado && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30">
                            <History className="w-3 h-3 inline mr-1" />
                            Archivada
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-zinc-500" />
                        <span className="text-white font-medium truncate">{orden.cliente_nombre || 'Sin nombre'}</span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-zinc-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(orden.created_at)}
                        </span>
                        {orden.cotizacion_numero && (
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            {orden.cotizacion_numero}
                          </span>
                        )}
                        {orden.origen === 'mi_jpm_online' && (
                          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-300" data-testid={`venta-online-${orden.numero}`}>Compra online</span>
                        )}
                      </div>
                    </div>

                    {/* Valores y progreso */}
                    <div className="flex flex-col items-end gap-2">
                      <div className="text-right">
                        <p className="text-xs text-zinc-500">Total</p>
                        <p className="text-lg font-bold text-white">{formatPrice(orden.total_final)}</p>
                      </div>
                      
                      {/* Barra de progreso */}
                      <div className="w-32">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-emerald-400">{porcentajePagado}%</span>
                          <span className="text-zinc-500">Pagado</span>
                        </div>
                        <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              porcentajePagado >= 100 
                                ? 'bg-emerald-500' 
                                : porcentajePagado > 0 
                                  ? 'bg-amber-500' 
                                  : 'bg-zinc-600'
                            }`}
                            style={{ width: `${Math.min(porcentajePagado, 100)}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        {orden.saldo_pendiente > 0 && (
                          <span className="text-red-400">
                            Saldo: {formatPrice(orden.saldo_pendiente)}
                          </span>
                        )}
                        {/* Botón eliminar - Solo Gerencia */}
                        {esGerencia && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              abrirModalEliminarVenta(orden);
                            }}
                            className="p-2 hover:bg-red-500/20 rounded-lg transition-colors group/del"
                            title="Eliminar venta"
                            data-testid={`ventas-eliminar-${orden.numero}`}
                          >
                            <Trash2 className="w-4 h-4 text-zinc-500 group-hover/del:text-red-400" />
                          </button>
                        )}
                        <ChevronRight className="w-5 h-5 text-zinc-500 group-hover:text-amber-400 transition-colors" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal Eliminar Venta (Borrado Lógico - Solo Gerencia) */}
        {showEliminarModal && ventaParaEliminar && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-zinc-800">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold flex items-center gap-2 text-red-400">
                    <Trash2 className="w-5 h-5" />
                    Eliminar Venta
                  </h2>
                  <button
                    onClick={() => {
                      setShowEliminarModal(false);
                      setVentaParaEliminar(null);
                    }}
                    className="p-2 hover:bg-zinc-800 rounded-lg"
                  >
                    <CloseIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Info de la venta */}
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <p className="text-sm text-zinc-400">Venta a eliminar</p>
                  <p className="font-semibold text-lg text-amber-400">{ventaParaEliminar.numero}</p>
                  <p className="text-sm text-zinc-300">{ventaParaEliminar.cliente_nombre}</p>
                  <p className="text-sm font-medium text-white">
                    Total: {formatPrice(ventaParaEliminar.total_final)}
                  </p>
                </div>
                
                {/* Dependencias */}
                {cargandoDependencias ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500"></div>
                    <span className="ml-2 text-zinc-400">Verificando dependencias...</span>
                  </div>
                ) : dependenciasVenta && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      Registros relacionados:
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                        <p className="text-2xl font-bold text-emerald-400">{dependenciasVenta.pagos_count}</p>
                        <p className="text-xs text-zinc-400">Pagos registrados</p>
                      </div>
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                        <p className="text-2xl font-bold text-emerald-400">{formatPrice(dependenciasVenta.total_pagado)}</p>
                        <p className="text-xs text-zinc-400">Monto pagado</p>
                      </div>
                    </div>
                    
                    {dependenciasVenta.puntos_canjeados > 0 && (
                      <div className="bg-purple-500/20 border border-purple-500/40 rounded-lg p-3 flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-purple-400" />
                        <span className="text-sm">
                          <strong>{dependenciasVenta.puntos_canjeados}</strong> puntos JPM canjeados (serán revertidos)
                        </span>
                      </div>
                    )}
                    
                    {!dependenciasVenta.puede_eliminar && (
                      <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3">
                        <p className="text-sm text-red-300 font-medium">
                          {dependenciasVenta.razon_bloqueo}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Formulario de eliminación */}
                {dependenciasVenta?.puede_eliminar && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">
                        Motivo de eliminación * (mín. 10 caracteres)
                      </label>
                      <textarea data-testid="ventas-eliminar-motivo"
                        value={motivoEliminacion}
                        onChange={(e) => setMotivoEliminacion(e.target.value)}
                        className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-red-500 focus:outline-none resize-none"
                        placeholder="Explique el motivo de la eliminación..."
                        rows={3}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">
                        Escriba "{ventaParaEliminar.numero}" para confirmar
                      </label>
                      <input data-testid="ventas-eliminar-confirmar-numero"
                        type="text"
                        value={confirmacionNumero}
                        onChange={(e) => setConfirmacionNumero(e.target.value)}
                        className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-red-500 focus:outline-none"
                        placeholder="Número de venta"
                      />
                    </div>
                    
                    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3">
                      <p className="text-xs text-zinc-400 flex items-center gap-2">
                        <History className="w-4 h-4" />
                        Este es un <strong className="text-amber-400">borrado lógico</strong>. El registro puede restaurarse desde "Registros Eliminados".
                      </p>
                    </div>
                  </>
                )}
              </div>
              
              <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
                <button data-testid="ventas-eliminar-cancelar-btn"
                  onClick={() => {
                    setShowEliminarModal(false);
                    setVentaParaEliminar(null);
                  }}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg"
                >
                  Cancelar
                </button>
                {dependenciasVenta?.puede_eliminar && (
                  <button data-testid="ventas-eliminar-confirmar-btn"
                    onClick={confirmarEliminacionVenta}
                    disabled={eliminando || motivoEliminacion.length < 10 || confirmacionNumero.trim().toUpperCase() !== ventaParaEliminar.numero.toUpperCase()}
                    className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {eliminando ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Eliminando...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Eliminar Venta
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default VentasList;
